import { useEffect, useState } from "react";
import { adminApi, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Check, Eye, X } from "lucide-react";

const STATUSES = ["new", "reviewed", "rejected"];

export default function ApplicationsList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [viewing, setViewing] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/job-applications");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function updateStatus(id, status) {
    try {
      await adminApi.patch(`/admin/job-applications/${id}`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  const filtered = items.filter(
    (a) => filter === "all" || a.status === filter
  );

  return (
    <div data-testid="applications-list" className="space-y-6">
      <div className="flex flex-wrap gap-2">
        {["all", ...STATUSES].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            data-testid={`app-filter-${s}`}
            className={`px-4 py-2 text-xs uppercase tracking-widest border transition-colors ${
              filter === s
                ? "bg-[var(--trio-text)] text-[var(--trio-bg)] border-[var(--trio-text)]"
                : "border-[var(--trio-border)] hover:border-[var(--trio-text)]"
            }`}
          >
            {s} {s !== "all" && `(${items.filter((a) => a.status === s).length})`}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-[var(--trio-muted)]">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-sm text-[var(--trio-muted)] py-12 text-center bg-[var(--trio-surface)]">
          No applications.
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((a) => (
            <article
              key={a.id}
              data-testid={`app-row-${a.id}`}
              className="bg-[var(--trio-surface)] border border-[var(--trio-border)] p-5 grid grid-cols-1 md:grid-cols-12 gap-4 items-start"
            >
              <div className="md:col-span-4">
                <span className="text-[10px] tracking-[0.25em] uppercase font-bold text-[var(--trio-primary)]">
                  {a.position}
                  {a.experience_years != null && ` · ${a.experience_years}y`}
                </span>
                <div className="font-display uppercase text-lg tracking-tight">
                  {a.name}
                </div>
                <div className="text-xs text-[var(--trio-muted)]">
                  <a href={`mailto:${a.email}`} className="hover:underline">
                    {a.email}
                  </a>
                  <span className="mx-1">·</span>
                  <a href={`tel:${a.phone}`} className="hover:underline">
                    {a.phone}
                  </a>
                </div>
              </div>
              <div className="md:col-span-5 text-sm text-[var(--trio-muted)] line-clamp-3">
                {a.resume}
              </div>
              <div className="md:col-span-3 flex flex-col gap-2 items-end">
                <StatusBadge status={a.status} />
                <div className="flex gap-1">
                  <IconBtn
                    title="View"
                    testid={`app-view-${a.id}`}
                    onClick={() => setViewing(a)}
                  >
                    <Eye className="w-4 h-4" />
                  </IconBtn>
                  <IconBtn
                    title="Reviewed"
                    testid={`app-reviewed-${a.id}`}
                    onClick={() => updateStatus(a.id, "reviewed")}
                  >
                    <Check className="w-4 h-4 text-emerald-700" />
                  </IconBtn>
                  <IconBtn
                    title="Reject"
                    testid={`app-reject-${a.id}`}
                    onClick={() => updateStatus(a.id, "rejected")}
                  >
                    <X className="w-4 h-4 text-red-700" />
                  </IconBtn>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent
          data-testid="app-detail-dialog"
          className="bg-[var(--trio-bg)] border-[var(--trio-border)] rounded-none max-w-2xl max-h-[85vh] overflow-y-auto"
        >
          <DialogHeader>
            <DialogTitle className="font-display uppercase text-2xl tracking-tight">
              {viewing?.name} · {viewing?.position}
            </DialogTitle>
          </DialogHeader>
          {viewing && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3 text-xs">
                <Pair label="Email" v={viewing.email} />
                <Pair label="Phone" v={viewing.phone} />
                <Pair label="Experience" v={viewing.experience_years ?? "—"} />
                <Pair label="Availability" v={viewing.availability ?? "—"} />
              </div>
              <Section title="Resume / Past Jobs">
                <pre className="whitespace-pre-wrap font-body text-sm bg-[var(--trio-surface)] p-4 border border-[var(--trio-border)] max-h-72 overflow-y-auto">
                  {viewing.resume}
                </pre>
              </Section>
              {viewing.cover_message && (
                <Section title="Why Trio?">
                  <p className="text-sm bg-[var(--trio-surface)] p-4 border border-[var(--trio-border)]">
                    {viewing.cover_message}
                  </p>
                </Section>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--trio-primary)] font-bold mb-2">
        {title}
      </div>
      {children}
    </div>
  );
}

function Pair({ label, v }) {
  return (
    <div>
      <div className="text-[9px] tracking-[0.3em] uppercase text-[var(--trio-muted)] mb-0.5">
        {label}
      </div>
      <div className="text-sm">{v}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const styles = {
    new: "bg-amber-100 text-amber-900 border-amber-300",
    reviewed: "bg-emerald-100 text-emerald-900 border-emerald-300",
    rejected: "bg-red-100 text-red-900 border-red-300",
  };
  return (
    <span
      className={`text-[10px] tracking-[0.25em] uppercase font-bold border px-2 py-1 ${
        styles[status] || ""
      }`}
    >
      {status}
    </span>
  );
}

function IconBtn({ children, onClick, title, testid }) {
  return (
    <button
      onClick={onClick}
      title={title}
      data-testid={testid}
      className="p-2 border border-[var(--trio-border)] hover:bg-[var(--trio-bg)] transition-colors"
    >
      {children}
    </button>
  );
}
